import { useEffect, useState } from 'react'
import type { Me } from '../lib/me'
import {
  readDismissals,
  recordDismissal,
  visibleNotices,
  loadActiveNotices,
  dismissNotice,
  type Notice,
  type DbNotice,
} from '../lib/notices'
import { messageOf } from '../lib/errors'
import { useHubLang } from '../lib/i18n'
import { shownIn } from '../lib/hubCopy'

/**
 * The stack of notices above the hub.
 *
 * Two sources rendered by one component — see lib/notices.ts for why the
 * build-state warnings stay in code while comms copy lives in the database.
 *
 * ── COMMS COPY COMES FIRST, BUILD WARNINGS LAST ─────────────────────────────
 *
 * Not a sort: two concatenated lists, in the order a reader should meet them.
 * A message from the company is the thing they are here for; "some numbers on
 * this page are invented" is a note to the five of us that happens to be on
 * screen. Ordering by tone would put the red one first, which is the opposite
 * of what it deserves.
 *
 * ── THE × IS SANCTIONED, NOT INVENTED ───────────────────────────────────────
 *
 * The design system's readme permits a bare × for dismiss, ↓ for download and
 * ⌕ for search, and nothing else; 046 used the same × on FilterChips. Removal
 * elsewhere stays WORDED ("Remove" on a grant, "Clear filters") because those
 * act on a RECORD. This acts on the thing you are looking at.
 *
 * ── THE MANAGER IS NOT HERE ANY MORE (SHL-33, David 10 Oct) ────────────────
 *
 * 039 rendered NoticeManager under this strip so a comms admin wrote a notice
 * while looking at what everybody sees, and 12 Sep put its switch ("Manage
 * notices") in the identity menu. On 10 Oct David moved authoring off the hub
 * altogether: it is #/admin/notices now (routes/AdminNotices.tsx). That page
 * renders THIS component as its live preview, re-read after every change, so
 * 039's benefit survives — you still write a notice while looking at it, just
 * not on the hub. Do not move the manager back in here on 039's strength.
 *
 * `preview` is that page's mode: no dismiss buttons (putting a notice away in
 * a preview would put it away for the admin on the real hub too), and an
 * empty strip says so instead of rendering nothing.
 */
export function Notices({
  notices,
  preview = false,
  emptyText,
}: {
  notices: Notice[]
  /** Kept for the callers' shape; nothing here reads the person any more. */
  me?: Me
  /** #/admin/notices: no dismiss buttons, and an empty strip says so. */
  preview?: boolean
  /** What a preview says when nothing is showing. */
  emptyText?: string
}) {
  // Code-notice dismissals, read once on mount: localStorage is not reactive
  // and nothing else in this tab writes it.
  const [dismissed, setDismissed] = useState(() => readDismissals())
  const [live, setLive] = useState<DbNotice[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function refresh() {
    setLive(await loadActiveNotices())
  }

  useEffect(() => {
    let alive = true
    loadActiveNotices()
      .then((n) => alive && setLive(n))
      // A notice strip that will not load is a missing strip, never a broken
      // hub — same posture as the rebrand counters on Home.
      .catch(() => alive && setLive([]))
    return () => {
      alive = false
    }
  }, [])

  async function putAway(contentId: string) {
    // Optimistic, then re-read. The row is gone from active_notices() the
    // moment the write lands, so there is nothing to reconcile — but the
    // re-read is what proves the write happened rather than assuming it.
    setLive((cur) => (cur ?? []).filter((n) => n.contentId !== contentId))
    try {
      await dismissNotice(contentId)
      await refresh()
    } catch (e) {
      setError(messageOf(e))
      await refresh()
    }
  }

  const showingCode = visibleNotices(notices, dismissed)
  const showingDb = live ?? []
  const nothing = showingDb.length === 0 && showingCode.length === 0

  // Nothing to show means no wrapper at all on the hub — an empty container
  // is still a difference. The preview says it is empty instead.
  if (nothing) return preview && emptyText ? <p className="empty">{emptyText}</p> : null

  return (
    <div className="notices-wrap">
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {!nothing && (
        // A list, so a screen reader announces how many there are before
        // reading the first — three stacked notices and one are different
        // situations.
        <ul className="notices" aria-label="Notices">
          {showingDb.map((n) => (
            <NoticeStripItem
              key={n.contentId}
              n={n}
              preview={preview}
              onDismiss={() => void putAway(n.contentId)}
            />
          ))}

          {showingCode.map((n) => (
            <li key={n.id} className={`phnotice phnotice--${n.tone}`}>
              <span className="phnotice__tag">{n.tag}</span>
              <div className="phnotice__main">
                <p className="phnotice__body">{n.body}</p>
                {n.action && (
                  <a className="btn btn--quiet btn--xs phnotice__action" href={n.action.href}>
                    {n.action.label}
                  </a>
                )}
              </div>
              {n.dismissible && !preview && (
                <button
                  type="button"
                  className="phnotice__dismiss"
                  aria-label={`Dismiss: ${n.tag}`}
                  onClick={() => setDismissed(recordDismissal(n.id, n.version))}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * One stored notice as a strip — the hub's, and "See how it looks" on
 * #/admin/notices (David, 12 Oct). One component so the preview cannot drift
 * from what people get.
 *
 * `look` is the admin's preview of a single notice: the × is drawn when the
 * notice can be dismissed (pinned or not is part of how it looks) but does
 * nothing, and the button is drawn but goes nowhere — leaving the page from a
 * preview, mid-draft, is not what a preview is for.
 */
export function NoticeStripItem({
  n,
  preview = false,
  look = false,
  onDismiss,
}: {
  n: DbNotice
  preview?: boolean
  look?: boolean
  onDismiss?: () => void
}) {
  const lang = useHubLang()
  return (
    <li className="phnotice phnotice--campaign">
      {n.tag && <span className="phnotice__tag">{n.tag}</span>}
      <div className="phnotice__main">
        <p className="phnotice__body">{n.body}</p>
        {n.ctaHref && n.ctaLabel && (look ? (
          <span className="btn btn--quiet btn--xs phnotice__action">{n.ctaLabel}</span>
        ) : (
          /* A real anchor, not a button with onClick. It goes to a
             place, so middle-click and open-in-new-tab work. The
             destination is constrained to an internal hash by a CHECK
             constraint in 057, not by trusting this render. */
          <a className="btn btn--quiet btn--xs phnotice__action" href={n.ctaHref}>
            {n.ctaLabel}
          </a>
        ))}
        {n.isFallback && (
          /* Same badge the feed carries. Without it a Spanish reader
             silently concludes the company posts in English, and nobody
             ever files the translation. */
          <span className="flag">{shownIn(lang, n.variantLocale)}</span>
        )}
      </div>
      {n.isDismissible && look && (
        <span className="phnotice__dismiss" aria-hidden="true">
          ×
        </span>
      )}
      {n.isDismissible && !preview && !look && (
        <button
          type="button"
          className="phnotice__dismiss"
          aria-label={`Dismiss: ${n.tag ?? 'notice'}`}
          onClick={onDismiss}
        >
          ×
        </button>
      )}
    </li>
  )
}
